/**
 * Shell entry point.
 *
 * A deliberately plain launcher for step 4: pick local, host or join. Step 5
 * replaces this with the lobby (room codes, team selection, game selection);
 * the mode functions it calls are already the ones the lobby will use.
 */
import { isRoomCode } from '@office/net';
import { type Teardown, startBot, startGuestRoom, startHostRoom, startLocal } from './modes.js';
import { BOT_LEVELS, type BotLevel } from '@office/sim';
import { allocateRoom } from './signaling.js';
import { isTouchDevice } from '../games/volley/touch.js';
import { BindingsPanel } from './bindings-ui.js';

type Mode = 'local' | 'bot' | 'host' | 'join';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  Object.assign(node, props);
  node.append(...children);
  return node;
}

function isBotLevel(v: string | null): v is BotLevel {
  return (BOT_LEVELS as readonly string[]).includes(v ?? '');
}

/** `room` doubles as the bot level for `?mode=bot&level=…`. */
function readRoute(): { mode: Mode; room: string } | null {
  const params = new URLSearchParams(location.search);
  const mode = params.get('mode');
  const room = (params.get('room') ?? '').toUpperCase();
  if (mode === 'local') return { mode, room: '' };
  if (mode === 'bot') {
    const level = params.get('level');
    return { mode, room: isBotLevel(level) ? level : 'medium' };
  }
  if ((mode === 'host' || mode === 'join') && isRoomCode(room)) return { mode, room };
  return null;
}

function setRoute(mode: Mode | null, room: string): void {
  const url = new URL(location.href);
  url.searchParams.delete('mode');
  url.searchParams.delete('room');
  url.searchParams.delete('level');
  if (mode) url.searchParams.set('mode', mode);
  if (mode === 'bot') url.searchParams.set('level', room);
  else if (mode && room) url.searchParams.set('room', room);
  history.replaceState(null, '', url);
}

async function main(): Promise<void> {
  const canvas = document.querySelector<HTMLCanvasElement>('#court');
  const boot = document.querySelector<HTMLElement>('#boot');
  const status = document.querySelector<HTMLElement>('#status');
  if (!canvas || !status) throw new Error('shell markup missing');

  const say = (text: string): void => {
    status.textContent = text;
  };

  let teardown: Teardown | null = null;

  // One way out of anything: back to the launcher. Shown whenever a mode is
  // running, over the lobby and over the match alike.
  const leave = el('button', { type: 'button', className: 'leave' }, 'Leave');
  leave.setAttribute('aria-label', 'leave and go back to the menu');
  leave.hidden = true;
  leave.addEventListener('click', () => {
    teardown?.();
    teardown = null;
    leave.hidden = true;
    setRoute(null, '');
    say('');
    showMenu();
  });
  document.body.append(leave);

  const launch = async (mode: Mode, room: string): Promise<void> => {
    teardown?.();
    teardown = null;
    setRoute(mode, room);
    say('starting…');
    if (mode === 'local') teardown = await startLocal(canvas, say);
    else if (mode === 'bot') teardown = await startBot(canvas, isBotLevel(room) ? room : 'medium', say);
    else if (mode === 'host') teardown = await startHostRoom(canvas, room, say);
    else teardown = await startGuestRoom(canvas, room, say);
    leave.hidden = false;
  };

  const route = readRoute();
  boot?.remove();
  if (route) {
    await launch(route.mode, route.room);
    return;
  }
  showMenu();
  say('');

  function showMenu(): void {
  const code = el('input', {
    className: 'code',
    placeholder: 'CODE',
    maxLength: 4,
    autocapitalize: 'characters',
    spellcheck: false,
  });

  const levelButtons = BOT_LEVELS.map((level) =>
    el(
      'button',
      {
        type: 'button',
        className: 'level',
        onclick: () => {
          menu.remove();
          void launch('bot', level);
        },
      },
      level,
    ),
  );

  const menu = el(
    'div',
    { className: 'menu' },
    el(
      'div',
      { className: 'brand' },
      el('h1', {}, 'Blob Volley'),
      el('p', { className: 'sub' }, 'Office Games'),
    ),
    el(
      'div',
      { className: 'group' },
      el('span', { className: 'label' }, 'Play against the computer'),
      el('div', { className: 'levels' }, ...levelButtons),
    ),
    el(
      'div',
      { className: 'group' },
      el('span', { className: 'label' }, 'Play with people'),
      el(
        'button',
        {
          type: 'button',
          className: 'primary',
          onclick: (event) => {
            const button = event.currentTarget as HTMLButtonElement;
            button.disabled = true;
            button.textContent = 'Opening a room…';
            void allocateRoom()
              .then((room) => {
                menu.remove();
                return launch('host', room);
              })
              .catch((err: unknown) => {
                button.disabled = false;
                button.textContent = 'Host a room';
                say(`Could not open a room: ${String(err)}`);
              });
          },
        },
        'Host a room',
      ),
      el(
        'div',
        { className: 'join' },
        code,
        el(
          'button',
          {
            type: 'button',
            onclick: () => {
              const room = code.value.trim().toUpperCase();
              if (!isRoomCode(room)) {
                code.classList.add('bad');
                return;
              }
              menu.remove();
              void launch('join', room);
            },
          },
          'Join',
        ),
      ),
      ...(isTouchDevice()
        ? []
        : [
            el(
              'button',
              {
                type: 'button',
                className: 'quiet',
                onclick: () => {
                  menu.remove();
                  void launch('local', '');
                },
              },
              'Two players, one keyboard',
            ),
          ]),
    ),
    ...(isTouchDevice()
      ? []
      : [
          el(
            'button',
            {
              type: 'button',
              className: 'quiet controls',
              onclick: () => {
                menu.hidden = true;
                panel.open();
              },
            },
            'Controls',
          ),
        ]),
    el(
      'p',
      { className: 'hint' },
      isTouchDevice()
        ? 'Turn the phone sideways to play.'
        : 'Hosting opens a room on this machine. Share the code or the link.',
    ),
  );

  const panel = new BindingsPanel(() => {
    menu.hidden = false;
  });
  const observer = new MutationObserver(() => {
    if (!menu.isConnected) {
      panel.destroy();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true });

  code.addEventListener('input', () => code.classList.remove('bad'));
  document.body.append(menu);
  }
}

void main().catch((err: unknown) => {
  console.error(err);
  const boot = document.querySelector<HTMLElement>('#boot');
  if (boot) boot.textContent = `Failed to start: ${String(err)}`;
});
